import { Box, Chip, Container, Grid, Pagination, Stack, Typography } from "@mui/material";
import ContentHeader from "../../components/layout/ContentHeader/ContentHeader";
import Section from "../../components/layout/Section/Section";
import ContentTitle from "../../components/layout/ContentTitle/ContentTitle";
import NewsCard from "../../components/display/NewsCard/NewsCard";
import { ChangeEvent, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiClient } from "../../services/api_client";
import NavBreadcrumbs from "../../components/navigation/NavBreadcrumbs/NavBreadcrumbs";
import { PAGE } from "../../constants/page";
import OtherSection from "../../components/display/OtherSection/OtherSection";
import Hr from "../../components/ui/Hr/Hr";

interface Props {
  children?: React.ReactElement<unknown>;
}

export default function News(props: Props) {
  /** 読み込み中判定 */
  const [isLoading, setIsLoading] = useState<boolean>(true)
  /** クエリパラメータ */
  const [searchParams, setSearchParams] = useSearchParams()
  /** 1ページに表示する件数 */
  const [count, setCount] = useState(9)
  /** ページ番号 */
  const [page, setPage] = useState(Number(searchParams.get("page")))
  /** お知らせリスト */
  const [newses, setNewses] = useState<Array<News>>([])
  /** タグリスト */
  const [tags, setTags] = useState<Array<string>>(process.env.REACT_APP_NEWS_TAGS?.split(",") ?? [])
  /** 選択中タグ */
  const [selectedTag, setSelectedTag] = useState<string>(searchParams.get("tag") ?? "")
  /** 全ページ数 */
  const [pageCount, setPageCount] = useState<number>(1)

  // お知らせ取得API
  const fetchData = async () => {
    // クエリパラメータ設定
    const queryParams: Array<Array<string>> = [["limit", String(count)]]
    // 絞り込み条件設定
    const filters: Array<string> = []
    // 2ページ目以降はoffsetを設定
    if (page > 1) queryParams.push(["offset", String((page - 1) * count)])
    // タグが選択中の場合はtagsで絞り込み
    if (selectedTag) filters.push(`tags[contains]${selectedTag}`)
    // プレビューモードを絞り込み
    if (process.env.REACT_APP_NEWS_PREVIEW_MODE === 'false') filters.push("preview[equals]false")
    // 絞り込み条件設定をANDで結合
    if (filters.length) queryParams.push(["filters", filters.join("[and]")])
    // 管理画面デフォルトの表示順でソート
    queryParams.push(["orders", "system:default"])
 
    const getNews = await apiClient.getNews({
      // mock: 'GetNews.json'
      queryParams: queryParams
    }).finally(() => {
      setIsLoading(false)
    })
    // 全ページ数を設定
    setPageCount(Math.ceil(getNews.totalCount / count))
    // お知らせを設定
    setNewses(getNews.contents)
  }

  /** 初期表示処理 */
  useEffect(() => {
    // 初期表示時は1ページ目を表示
    if (!searchParams.get("page")) {
      setPage(1)
      setSearchParams({
        page: '1',
        tag: selectedTag
      })
    }
  }, [])

  /** ページ切り替えまたはタグ切り替え時処理 */
  useEffect(() => {
    // お知らせ取得処理
    fetchData()
  }, [page, selectedTag])

  /** ページ切り替え処理 */
  const handlePage = async (event: ChangeEvent<unknown>, value: number) => {
    setPage(value)
    setSearchParams({
      page: String(value),
      tag: selectedTag
    })
    window.scroll({
      top: 0,
    })
  }

  /** タグ切り替え処理 */
  const handleTag = async (event: ChangeEvent<unknown>, tag: string) => {
    // 同じタグが選択された場合は初期化
    const setTag = selectedTag === tag ? "" : tag
    setSelectedTag(setTag)
    setSearchParams({
      page: "1", // ページ数は初期化
      tag: setTag
    })
    setPage(1)
  }

  return (
    <>
      <Section color="dark">
        <ContentHeader title={PAGE.NEWS.NAME} subTitle={PAGE.NEWS.TEXT} />
      </Section>

      <Section color="light" padding="1rem 0">
        <Container>
          <NavBreadcrumbs />
        </Container>
      </Section>

      <Section color="light">
        <Container>
          <ContentTitle title={'ALL NEWS'} subTitle={'すべてのニュース'} />

          <Stack direction="row" spacing={1} marginTop={'3rem'}>
            {tags.map((t, i) =>
              <Chip
                sx={{ marginRight: '0.25rem' }}
                key={i}
                label={t}
                color="primary"
                variant={selectedTag === t ? 'filled' : 'outlined'}
                onClick={(e) => handleTag(e, t)}
              />
            )}
          </Stack>

          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '2rem 0' }}>
            <Pagination
              count={pageCount}
              page={page}
              onChange={handlePage}
              size="large"
              color="primary"
            />
          </Box>
          {isLoading ?
            (
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 12, md: 4 }} sx={{ padding: '0.5rem 0' }}>
                  <NewsCard isLoading={true} />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 4 }} sx={{ padding: '0.5rem 0' }}>
                  <NewsCard isLoading={true} />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 4 }} sx={{ padding: '0.5rem 0' }}>
                  <NewsCard isLoading={true} />
                </Grid>
              </Grid>
            )
            :
            (
              <Grid container spacing={2}>
                {newses.length ? newses.map((news, index) => {
                  return (
                    <Grid key={index} size={{ xs: 12, sm: 12, md: 4 }} sx={{ padding: '0.5rem 0' }}>
                      <NewsCard news={news} />
                    </Grid>
                  )
                })
                  :
                  <Grid size={{ sm: 12 }} sx={{ padding: '1.5rem' }}>
                    <Typography variant="body2" component="p" fontSize={'1.25rem'} >
                      {'現在お知らせはありません。'}
                    </Typography>
                  </Grid>
                }
              </Grid>
            )
          }
          < Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '2rem 0' }}>
            <Pagination
              count={pageCount}
              page={page}
              onChange={handlePage}
              size="large"
              color="primary"
            />
          </Box>
        </Container>
      </Section >

      <Hr />

      <Section color='dark' height={'300px'} backgroundImage='url(/assets/images/top.jpg)' isBackgroundCover>
        <Container>
          <OtherSection isObserver={false} />
        </Container>
      </Section>
    </>
  )
}